import React, {useEffect, useRef, useState} from 'react';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {
  getRebatePaymentPageResult,
  initApplyPageContext,
  ViewEntityProps,
} from '@/pages/contract/RebatePaymentApply/components/data';
import {WdiCardTitle, WdiModalAppResult} from '@/components/WdiControls';
import {ResponseStatus} from '@/utils/request';
import {Col, Form, Input, message, Row} from 'antd';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {PageType} from '@/services/basic/pagetype';
import DataStore from '@/components/GridWidget/DataStore';
import {Enum} from '@/enums/system';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {FormGoods, FormSp} from '@/services/sales/sp';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {RebatePaymentDetailsForFgApplyPanel} from '@/pages/contract/RebatePaymentApply/components/detail';
import {VatType} from '@/services/mdm/vattype';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import SPWarningInfoApp from "@/pages/sales/sp/components/WarningInformation";
import {WarningInfoAppProps} from "@/components/WdiBus/PanelForWarning";


export const RebatePaymentFgApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<API.PageType>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [goodsDataSource, setGoodsDataSource] = useState<any[]>([]);
  const [vatType, setVatType] = useState<API.VatType>();
  const [editorRef, setEditorRef] = useState<any>();
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };


  useEffect(() => {
    (async () => {
      const params = props.data;

      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(viewEntity.formSp.fileName, viewEntity.formSp.filePath)
          });

          FormGoods.getForApply(viewEntity.formSp.formSpId || 0).then(responseResult => {
            setGoodsDataSource(DataStore.init(responseResult.data));
          })
        }
        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodName: viewEntity.paymentMethod.name,
          });
        }

        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }
        if (viewEntity.paymentMethodId == Enum.PaymentMethod.FEE_POOL) {
          VatType.getVAT13().then((res) => {
            formRef.current?.setFieldsValue({
              vatTypeId: res.data.vatTypeId,
              vatTypeName: res.data.vatTypeName,
            });
            setVatType(res.data);
          });
        } else {
          VatType.getVAT0().then((res) => {
            formRef.current?.setFieldsValue({
              vatTypeId: res.data.vatTypeId,
              vatTypeName: res.data.vatTypeName,
            });
            setVatType(res.data);
          });
        }
        if (viewEntity.formSpDetails) {
          setDataSource(DataStore.init(viewEntity.formSpDetails));
        }

        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = "返利核销申请";
      if (entity.formSp) {
        title += '【编辑】'
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        initialValues={{invoiceStatus: Enum.InvoiceStatus.NON_INVOICE}}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl="/contract/myrebateinfo"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId =
                    type === ApplyFooterType.Draft
                      ? Enum.FormStatus.DRAFT
                      : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getRebatePaymentPageResult(
                    values,
                    entity,
                    pageType,
                    statusId,
                    dataSource,
                    [],
                    [],
                    goodsDataSource,
                  );
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSp.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data,
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSp.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle title="返利核销信息"/>}>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={4}>
              <Form.Item name="vatTypeId" label="税率类型" hidden={true}>
                <Input readOnly/>
              </Form.Item>
              <Form.Item
                name="vatTypeName"
                label="可抵扣税率类型(普票为0)"
                rules={[{required: true, message: '税率类型不能为空'}]}
              >
                <Input readOnly/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="paymentMethodName" label="支付方式">
                <Input readOnly/>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="核销备注">
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <RebatePaymentDetailsForFgApplyPanel
          data={{
            paymentMethodId: entity?.paymentMethodId,
            ecId: entity?.ecId,
            pageTypeId: entity?.pageTypeId,
            vatType: vatType,
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
      </ProForm>
    </>
  );
};
